import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { WebsiteElement } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { InfoPanelData } from '../components/template/panels/info-panel/info-panel.component';
import { TestimonialsData } from '../components/template/panels/testimonials-panel/testimonials-panel.component';
import { BookingData } from '../components/template/panels/booking-panel/booking-panel.component';
import { ContactData } from '../components/template/panels/contact-panel/contact-panel.component';
import { FooterData } from '../components/footer-panel/footer-panel.component';
import { HeaderData } from '../components/header-panel/header-panel.component';
import {
  buttonNavigationToApiLink,
  buttonNavigationToHashLink,
  linkToButtonNavigation,
} from '../shared/panel-button-navigation';
import { BrandingData } from '../components/branding-panel/branding-panel.component';
import { TemplatePopulatorService } from './template-populator.service';
import {
  toOperatingHourStrings,
  toOperatingHoursSchedule,
} from '../../shared/utils/map-contact-business-hours.util';

/**
 * Mutable context bag for info/content panel operations.
 */
export interface InfoContext {
  json: any;
  elements: WebsiteElement[];
  templateData: any;
  infoPanelData: InfoPanelData;
  testimonialsPanelData: TestimonialsData;
  bookingPanelData: BookingData;
  contactPanelData: ContactData;
  footerData: FooterData;
  headerData: HeaderData;
  brandingData: BrandingData;
  website: any;
  pages: any[];
  currentPageType: string;
  showInfoPanel: boolean;
  showTestimonialsPanel: boolean;
  showBookingPanel: boolean;
  showFooterPanel: boolean;
  showHeaderPanel: boolean;
  showContactPanel: boolean;
  currentInfoSectionKey: string;
  scheduleAutosave(paths: string[]): void;
  recordStateChange(description: string): void;
}

/**
 * Handles change/save handlers for info, testimonials, booking,
 * contact, header, and footer panels.
 * Methods operate on an {@link InfoContext} passed by the component.
 */
@Injectable({ providedIn: 'root' })
export class InfoContentService {
  constructor(
    private logger: LoggerService,
    private templatePopulator: TemplatePopulatorService
  ) {}

  private getFullAddressFromContactData(data: ContactData): string {
    if (typeof data.contactInfo?.address === 'string') {
      return data.contactInfo.address;
    }
    if (data.contactInfo?.address) {
      const addr = data.contactInfo.address as any;
      const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter((p: string) => p);
      return parts.join(', ');
    }
    return '';
  }

  closeInfoPanel(ctx: InfoContext): void {
    ctx.showInfoPanel = false;
  }
  /**
   * Updates the active info panel data in context, syncs the corresponding section in the page JSON, and refreshes the matching website element for live preview.
   * @example
   * nInfoPanelChange(data, ctx)
   * undefined
   * @param {InfoPanelData} data - The updated info panel data to apply.
   * @param {InfoContext} ctx - The current info panel context containing panel state, page JSON, and website elements.
   * @returns {void} Nothing is returned.
   **/
  onInfoPanelChange(data: InfoPanelData, ctx: InfoContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showInfoPanel) {
      return;
    }

    ctx.infoPanelData = data;
    this.logger.debug(
      `📝 Info panel change detected for section: ${ctx.currentInfoSectionKey}`,
      data
    );

    // Update currentJsonData for API sync (live update like hero panel)
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    // Get the correct info section variant (not always info_section)
    const sectionKey = ctx.currentInfoSectionKey;
    if (!ctx.json.page.sections[sectionKey]) {
      ctx.json.page.sections[sectionKey] = {};
    }
    const infoSection = ctx.json.page.sections[sectionKey];

    // Store info section data
    // Update title - ALWAYS set the label and style
    if (!infoSection.title || typeof infoSection.title !== 'object') {
      infoSection.title = {};
    }
    infoSection.title.label = data.title;
    infoSection.title.style = {
      fontWeight: data.titleStyle?.fontWeight || null,
      fontStyle: data.titleStyle?.fontStyle || null,
      textDecoration: data.titleStyle?.textDecoration || null,
      textAlign: data.titleStyle?.textAlign || null,
    };
    infoSection.description = data.description;
    // Store subtitle if provided (for layout_5)
    if (data.subtitle) {
      infoSection.subtitle = data.subtitle;
    }
    infoSection.button = {
      ...(infoSection.button || {}),
      label: data.buttonText,
      link: data.buttonNavigation,
    };
    // Convert imageFit to scaletype for API
    const imageScaletype = data.imageFit || data.image?.scaletype || 'fit';
    infoSection.image = {
      ...(infoSection.image || {}),
      src: data.imageUrl,
      scaletype: imageScaletype,
    };
    // Keep active state if it exists
    if (infoSection.active === undefined) {
      infoSection.active = true;
    }

    // Convert layout to proper format (layout_X)
    let layoutType: string = data.layout || 'layout_1';
    if (typeof layoutType === 'string') {
      if (/^[1-5]$/.test(layoutType)) {
        layoutType = `layout_${layoutType}`;
      } else {
        layoutType = 'layout_1';
      }
    } else {
      layoutType = 'layout_1';
    }

    // Store layout value
    infoSection.layout_type = layoutType;

    // Preserve layout object if it exists, otherwise create based on layout_type
    if (!infoSection.layout) {
      infoSection.layout = {
        type: layoutType,
        grid_columns: 2,
        image_alignment: layoutType === 'layout_3' ? 'left' : 'right',
        padding: '100px 120px',
      };
    } else {
      // Update layout type
      infoSection.layout.type = layoutType;
    }
    // Update background_color in layout
    infoSection.layout.background_color = data.backgroundColor;

    // Update the correct info element in websiteElements for live preview
    // Element ID: 'welcome' for info_section, 'welcome_info_section_1' for info_section_1, etc.
    let elementId = 'welcome';
    if (sectionKey !== 'info_section') {
      elementId = `welcome_${sectionKey}`;
    }

    const infoElement = this.findInfoElement(ctx.elements, sectionKey, elementId);
    if (infoElement) {
      // Ensure image object exists
      if (!infoElement.content.image) {
        infoElement.content.image = {};
      }
      // Get imageScaletype for element update
      const imageScaletype = data.imageFit || data.image?.scaletype || 'fit';

      // CRITICAL: Create a new content object to trigger change detection
      // This ensures Angular detects the change and updates the preview
      infoElement.content = {
        ...infoElement.content,
        title: data.title,
        titleStyle: data.titleStyle || undefined, // CRITICAL: Include titleStyle for live preview updates
        description: data.description,
        subtitle: data.subtitle,
        imageUrl: data.imageUrl,
        image: {
          ...infoElement.content.image,
          src: data.imageUrl,
          scaletype: (imageScaletype as any) || 'fit',
        },
        buttonText: data.buttonText,
        buttonLink: data.buttonNavigation, // Use buttonLink (matches WebsiteElement interface)
        layout: data.layout,
        backgroundColor: data.backgroundColor,
      };

      // CRITICAL: Force change detection by creating new array and element references
      // This ensures the preview component detects the change
      const elementIndex = ctx.elements.findIndex(el => el.id === infoElement.id);
      if (elementIndex !== -1) {
        ctx.elements = [
          ...ctx.elements.slice(0, elementIndex),
          { ...ctx.elements[elementIndex], content: { ...infoElement.content } },
          ...ctx.elements.slice(elementIndex + 1),
        ];
      } else {
        // Fallback: just recreate the array
        ctx.elements = [...ctx.elements];
      }
    } else {
      this.logger.warn(`⚠️ Element ${elementId} not found for section ${sectionKey}`);
    }

    // 🔥 FIX: Record state AFTER all data is updated
    ctx.recordStateChange('Info section updated');

    // Schedule autosave (like hero panel does)
    this.logger.debug('💾 Scheduling autosave for info section:', sectionKey);
    ctx.scheduleAutosave([`/page/sections/${sectionKey}`]);
  }
  /**
   * Saves info panel data into the current section state and syncs the corresponding JSON structure for autosave.
   * @example
   * nInfoPanelSave(data, ctx)
   * void
   * @param {InfoPanelData} data - The info panel data to persist.
   * @param {InfoContext} ctx - The info editing context containing section state and JSON data.
   * @returns {void} This function does not return a value.
   **/
  onInfoPanelSave(data: InfoPanelData, ctx: InfoContext): void {
    ctx.infoPanelData = data;
    this.logger.debug(
      `💾 Info panel SAVE triggered for section: ${ctx.currentInfoSectionKey}`,
      data
    );

    // Get the correct section key
    const sectionKey = ctx.currentInfoSectionKey;

    // Determine element ID from section key
    let elementId = 'welcome';
    if (sectionKey !== 'info_section') {
      elementId = `welcome_${sectionKey}`;
    }

    // Convert imageFit to scaletype for API
    const imageScaletype = data.imageFit || data.image?.scaletype || 'fit';

    // Update the correct info/welcome element
    const infoElement = this.findInfoElement(ctx.elements, sectionKey, elementId);
    if (infoElement) {
      // Ensure image object exists
      if (!infoElement.content.image) {
        infoElement.content.image = {};
      }
      infoElement.content = {
        ...infoElement.content,
        title: data.title,
        description: data.description,
        subtitle: data.subtitle,
        imageUrl: data.imageUrl,
        image: {
          ...infoElement.content.image,
          src: data.imageUrl,
          scaletype: (imageScaletype as any) || 'fit',
        },
        buttonText: data.buttonText,
        layout: data.layout,
        backgroundColor: data.backgroundColor,
      };
      // Force change detection
      const elementIndex = ctx.elements.findIndex(el => el.id === infoElement.id);
      if (elementIndex !== -1) {
        ctx.elements = [
          ...ctx.elements.slice(0, elementIndex),
          { ...ctx.elements[elementIndex], content: { ...infoElement.content } },
          ...ctx.elements.slice(elementIndex + 1),
        ];
      } else {
        ctx.elements = [...ctx.elements];
      }
    } else {
      this.logger.warn(`⚠️ Element ${elementId} not found for section ${sectionKey}`);
    }

    // Update currentJsonData for API sync
    if (!ctx.json) ctx.json = {};
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    // Get the correct info section variant (not always info_section)
    if (!ctx.json.page.sections[sectionKey]) {
      ctx.json.page.sections[sectionKey] = {};
    }
    const infoSection = ctx.json.page.sections[sectionKey];

    if (!infoSection.title || typeof infoSection.title !== 'object') {
      infoSection.title = {};
    }
    infoSection.title.label = data.title;
    infoSection.title.style = {
      fontWeight: data.titleStyle?.fontWeight || null,
      fontStyle: data.titleStyle?.fontStyle || null,
      textDecoration: data.titleStyle?.textDecoration || null,
      textAlign: data.titleStyle?.textAlign || null,
    };
    infoSection.description = data.description;
    // Store subtitle if provided (for layout_5)
    if (data.subtitle) {
      infoSection.subtitle = data.subtitle;
    }
    infoSection.button = {
      ...(infoSection.button || {}),
      label: data.buttonText,
      link: data.buttonNavigation,
    };
    infoSection.image = {
      ...(infoSection.image || {}),
      src: data.imageUrl,
      scaletype: imageScaletype,
    };
    // Keep active state if it exists
    if (infoSection.active === undefined) {
      infoSection.active = true;
    }

    // Convert layout to proper format (layout_X)
    let layoutType: string = data.layout || 'layout_1';
    if (typeof layoutType === 'string') {
      if (/^[1-5]$/.test(layoutType)) {
        layoutType = `layout_${layoutType}`;
      } else {
        layoutType = 'layout_1';
      }
    } else {
      layoutType = 'layout_1';
    }

    // Store layout value
    infoSection.layout_type = layoutType;

    // Preserve layout object if it exists, otherwise create based on layout_type
    if (!infoSection.layout) {
      infoSection.layout = {
        type: layoutType,
        grid_columns: 2,
        image_alignment: layoutType === 'layout_3' ? 'left' : 'right',
        padding: '100px 120px',
      };
    } else {
      // Update layout type
      infoSection.layout.type = layoutType;
    }
    // Update background_color in layout
    infoSection.layout.background_color = data.backgroundColor;

    // Refresh editable JSON string for the quick editor

    // Schedule autosave with correct section key
    this.logger.debug('💾 Scheduling autosave for info section:', sectionKey);
    ctx.scheduleAutosave([`/page/sections/${sectionKey}`]);
  }
  /**
   * Updates the testimonials section data in the editor state and preview when the testimonials panel is open.
   * @example
   * nTestimonialsChange(data, ctx)
   * void
   * @param {TestimonialsData} data - Testimonials content and style data to apply.
   * @param {InfoContext} ctx - Editor/context object used to update JSON state, preview elements, and autosave.
   * @returns {void} Nothing is returned.
   **/
  onTestimonialsChange(data: TestimonialsData, ctx: InfoContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showTestimonialsPanel) {
      return;
    }

    ctx.testimonialsPanelData = data;

    if (!ctx.json) ctx.json = {};

    // Ensure page.sections structure exists
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    // Update in page.sections.testimonials_section (new structure)
    if (!ctx.json.page.sections.testimonials_section) {
      ctx.json.page.sections.testimonials_section = {};
    }
    const testimonialsSection = ctx.json.page.sections.testimonials_section;

    const testimonialsTitleStyle = data.titleStyle
      ? {
          fontWeight: data.titleStyle.fontWeight || null,
          fontStyle: data.titleStyle.fontStyle || null,
          textDecoration: data.titleStyle.textDecoration || null,
          textAlign: data.titleStyle.textAlign || null,
        }
      : null;

    if (testimonialsTitleStyle) {
      if (!testimonialsSection.title || typeof testimonialsSection.title !== 'object') {
        testimonialsSection.title = { label: data.title, style: testimonialsTitleStyle };
      } else {
        testimonialsSection.title.label = data.title;
        testimonialsSection.title.style = testimonialsTitleStyle;
      }
    } else if (
      typeof testimonialsSection.title === 'object' &&
      testimonialsSection.title !== null
    ) {
      testimonialsSection.title.label = data.title;
    } else {
      testimonialsSection.title = { label: data.title };
    }

    testimonialsSection.description = data.subtitle || '';
    testimonialsSection.subtitle = data.subtitle || '';
    testimonialsSection.testimonials = data.testimonials.map((t: any) => ({
      id: t.id || t.name?.toLowerCase().replace(/\s+/g, '-') || 'testimonial',
      name: t.name,
      review: t.text,
      rating: t.rating || 5,
      avatar: t.avatar,
    }));

    // Keep active state if it exists
    if (testimonialsSection.active === undefined) {
      testimonialsSection.active = true;
    }

    // Preserve layout if it exists
    if (!testimonialsSection.layout) {
      testimonialsSection.layout = {
        alignment: 'center',
        padding: '80px 100px',
        background_color: data.backgroundColor || '#F9FAFB',
        card_color: data.cardColor || '#FFFFFF',
      };
    } else {
      // Update background color and card color in layout if it exists
      if (testimonialsSection.layout) {
        testimonialsSection.layout.background_color =
          data.backgroundColor || testimonialsSection.layout.background_color || '#F9FAFB';
        testimonialsSection.layout.card_color =
          data.cardColor || testimonialsSection.layout.card_color || '#FFFFFF';
      }
    }

    // Update preview element
    const tElement = ctx.elements.find(e => e.id === 'testimonials');
    if (tElement) {
      tElement.content = {
        title: data.title,
        titleStyle: data.titleStyle || undefined,
        description: data.subtitle,
        testimonials: testimonialsSection.testimonials.map((t: any) => ({
          id: t.id,
          text: t.review || t.text,
          author: {
            name: t.name,
            avatar: t.avatar,
          },
          rating: t.rating || 5,
        })),
        cardStyle: data.cardStyle,
        cardColor: data.cardColor,
        backgroundColor: data.backgroundColor,
      } as any;

      // Force change detection by creating a new array reference
      ctx.elements = [...ctx.elements];
    }

    // Update editable JSON
    try {
      /* intentionally empty */
    } catch {
      /* ignore */
    }

    // 🔥 FIX: Record state AFTER all data is updated
    ctx.recordStateChange('Testimonials updated');

    // Trigger autosave
    ctx.scheduleAutosave(['/page/sections/testimonials_section']);
  }
  /**
   * Updates the booking section data, preview content, and autosave state when booking details change.
   * @example
   * nBookingChange(data, ctx)
   * undefined
   * @param {BookingData} data - Booking section data to apply to the page and preview.
   * @param {InfoContext} ctx - Context containing panel state, JSON data, elements, and autosave utilities.
   * @returns {void} Does not return a value.
   */
  onBookingChange(data: BookingData, ctx: InfoContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showBookingPanel) {
      return;
    }

    ctx.bookingPanelData = data;

    if (!ctx.json) ctx.json = {};

    // Ensure page.sections structure exists
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    // Update in page.sections.booking_section (new structure)
    if (!ctx.json.page.sections.booking_section) {
      ctx.json.page.sections.booking_section = {};
    }
    const bookingSection = ctx.json.page.sections.booking_section;

    const bookingTitleStyle = data.titleStyle
      ? {
          fontWeight: data.titleStyle.fontWeight || null,
          fontStyle: data.titleStyle.fontStyle || null,
          textDecoration: data.titleStyle.textDecoration || null,
          textAlign: data.titleStyle.textAlign || null,
        }
      : null;

    if (bookingTitleStyle) {
      if (!bookingSection.title || typeof bookingSection.title !== 'object') {
        bookingSection.title = { label: data.title, style: bookingTitleStyle };
      } else {
        bookingSection.title.label = data.title;
        bookingSection.title.style = bookingTitleStyle;
      }
    } else if (typeof bookingSection.title === 'object' && bookingSection.title !== null) {
      bookingSection.title.label = data.title;
    } else {
      bookingSection.title = { label: data.title };
    }

    bookingSection.subtitle = data.subtitle || '';

    // Update button structure
    if (data.button) {
      bookingSection.button = {
        ...(bookingSection.button || {}),
        label: data.button.text || 'Book Appointment',
        action: '/submit-booking',
        method: 'POST',
        style: {
          ...(bookingSection.button?.style || {}),
          background:
            data.button.backgroundColor !== undefined
              ? data.button.backgroundColor
              : bookingSection.button?.style?.background || '#1E40AF',
          color:
            data.button.textColor !== undefined
              ? data.button.textColor
              : bookingSection.button?.style?.color || '#FFFFFF',
        },
      };
    } else if (bookingSection.button) {
      // Preserve existing button if no new button data provided
      // Keep the existing button structure
    }

    // Keep active state if it exists
    if (bookingSection.active === undefined) {
      bookingSection.active = true;
    }

    // Preserve layout if it exists
    if (!bookingSection.layout) {
      bookingSection.layout = {
        alignment: 'center',
        padding: '100px 120px',
        background_color: data.backgroundColor !== undefined ? data.backgroundColor : '#F9FAFB',
      };
    } else if (data.backgroundColor !== undefined) {
      bookingSection.layout.background_color = data.backgroundColor;
    }

    // Preserve form_fields if they exist
    if (!bookingSection.form_fields) {
      bookingSection.form_fields = [];
    }

    // Update preview element
    const bElement = ctx.elements.find(e => e.id === 'booking' || e.type === 'booking');
    if (bElement) {
      // Ensure formFields is never empty - use defaults if empty
      const formFields =
        bookingSection.form_fields && bookingSection.form_fields.length > 0
          ? bookingSection.form_fields
          : this.templatePopulator.getDefaultFormFields();

      const updatedContent = {
        ...bElement.content,
        title: data.title,
        titleStyle: data.titleStyle || undefined,
        description: data.subtitle,
        formFields: formFields,
        formTheme: data.formTheme,
        button: data.button
          ? {
              text: data.button.text,
              backgroundColor: data.button.backgroundColor,
              textColor: data.button.textColor,
            }
          : (bElement.content as any)?.button || {
              text: 'Book Appointment',
              backgroundColor: '#1B5BFF',
              textColor: '#FFFFFF',
            },
        backgroundColor: data.backgroundColor,
      } as any;

      const elementIndex = ctx.elements.findIndex(el => el.id === bElement.id);
      if (elementIndex !== -1) {
        ctx.elements = [
          ...ctx.elements.slice(0, elementIndex),
          { ...ctx.elements[elementIndex], content: { ...updatedContent } },
          ...ctx.elements.slice(elementIndex + 1),
        ];
      } else {
        bElement.content = updatedContent;
        ctx.elements = [...ctx.elements];
      }
    }

    // Update editable JSON
    try {
      /* intentionally empty */
    } catch {
      /* ignore */
    }

    // 🔥 FIX: Record state AFTER all data is updated
    ctx.recordStateChange('Booking section updated');

    // Trigger autosave
    ctx.scheduleAutosave(['/page/sections/booking_section']);
  }
  /**
   * Updates the footer data in the active context, synchronizing website-level JSON, page-level fallback data, and the live preview when the footer panel is open.
   * @example
   * nFooterChange(data, ctx)
   * void
   * @param {FooterData} data - The updated footer data to apply.
   * @param {InfoContext} ctx - The context containing footer state, JSON mirrors, website data, and preview elements.
   * @returns {void} No return value.
   **/
  onFooterChange(data: FooterData, ctx: InfoContext): void {
    // Only process panel edits when the panel is actually open
    if (!ctx.showFooterPanel) {
      return;
    }
    this.persistFooter(data, ctx);
  }

  /**
   * Persist footer data (including Quick links) even when the footer panel is closed.
   * Used when header page visibility changes so footer links stay in sync.
   */
  persistFooter(data: FooterData, ctx: InfoContext): void {
    ctx.footerData = data;

    if (!ctx.json) ctx.json = {};

    // Get existing footer data to preserve properties
    const existingFooter = ctx.website?.json_data?.website?.footer || {};

    // Prepare quick_links in API format (with column_1 and column_2)
    // Split quickLinks into two columns if needed
    const quickLinksArray = data.quickLinks || [];
    const midPoint = Math.ceil(quickLinksArray.length / 2);
    const quick_links = {
      title:
        data.quickLinksTitle !== undefined
          ? data.quickLinksTitle
          : existingFooter.quick_links?.title || 'Quick links',
      column_1: quickLinksArray.slice(0, midPoint).map((link: any) => ({
        label: link.label || '',
        link: link.link || '#',
      })),
      column_2: quickLinksArray.slice(midPoint).map((link: any) => ({
        label: link.label || '',
        link: link.link || '#',
      })),
      style: existingFooter.quick_links?.style || {
        text_color: '#FFFFFF',
        link_color: '#FFFFFF',
      },
    };

    // Prepare social_links in API format (with title and platforms)
    const social_links = {
      title:
        data.socialLinksTitle !== undefined
          ? data.socialLinksTitle
          : existingFooter.social_links?.title || 'Connect with us',
      platforms: (data.socialLinks || []).map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      })),
      style: existingFooter.social_links?.style || {
        icon_color: '#FFFFFF',
        icon_size: '24px',
      },
    };

    // Update JSON mirror - footer data is stored at website.json_data.website.footer (API format)
    const fJson: any = {
      // Preserve existing properties
      ...existingFooter,
      // Update logo
      logo: {
        type: data.logo?.image ? 'image' : 'text',
        text: data.logo?.text || 'way',
        link: (data.logo as any)?.link || '/',
        url: data.logo?.image || '',
        style: existingFooter.logo?.style || {
          background: 'transparent',
          border: '1px solid #3B82F6',
          color: '#FFFFFF',
          padding: '10px 15px',
          border_radius: '4px',
          font_size: '24px',
          font_weight: '600',
        },
      },
      // Update quick_links in API format
      quick_links: quick_links,
      // Update social_links in API format
      social_links: social_links,
      // Update copyright
      copyright: {
        text:
          data.copyright || `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`,
        style: existingFooter.copyright?.style || {
          text_color: '#A3A3A3',
          font_size: '14px',
        },
      },
      // Update theme - ensure background_color is always updated when provided
      theme: {
        ...existingFooter.theme,
        background_color:
          data.backgroundColor !== undefined
            ? data.backgroundColor
            : existingFooter.theme?.background_color || '#1F2937',
        text_color: existingFooter.theme?.text_color || '#FFFFFF',
        link_color: existingFooter.theme?.link_color || '#FFFFFF',
        separator_color: existingFooter.theme?.separator_color || 'rgba(255, 255, 255, 0.1)',
      },
    };

    // Update website-level footer (shared across all pages) - HIGHEST PRIORITY
    if (!ctx.website) ctx.website = {};
    if (!ctx.website.json_data) ctx.website.json_data = {};
    if (!ctx.website.json_data.website) ctx.website.json_data.website = {};
    ctx.website.json_data.website.footer = fJson;

    // Also update currentJsonData for autosave
    if (!ctx.json.website) ctx.json.website = {};
    if (!ctx.json.website.footer) ctx.json.website.footer = {};
    ctx.json.website.footer = { ...ctx.json.website.footer, ...fJson };

    // Also update at page level for backward compatibility
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    ctx.json.page.sections.footer = {
      logo: data.logo,
      quickLinks: data.quickLinks,
      quickLinksTitle: data.quickLinksTitle,
      socialLinks: data.socialLinks,
      socialLinksTitle: data.socialLinksTitle,
      copyright: data.copyright,
      backgroundColor: data.backgroundColor,
    };

    // Get theme colors from existing footer or use defaults
    const textColor = existingFooter.theme?.text_color || '#FFFFFF';
    const linkColor = existingFooter.theme?.link_color || '#FFFFFF';
    const separatorColor = existingFooter.theme?.separator_color || 'rgba(255, 255, 255, 0.1)';

    // Update preview element for Home page
    const footerElement = ctx.elements.find(e => e.id === 'footer');
    if (footerElement) {
      // Create new content object to trigger change detection
      footerElement.content = {
        ...footerElement.content,
        logo: data.logo,
        quickLinks: data.quickLinks,
        quickLinksTitle: data.quickLinksTitle,
        socialLinks: data.socialLinks,
        socialLinksTitle: data.socialLinksTitle,
        copyright: data.copyright,
        backgroundColor: data.backgroundColor,
        textColor: textColor,
        linkColor: linkColor,
        separatorColor: separatorColor,
      } as any;
    }

    // Force update websiteElements array to trigger change detection
    ctx.elements = [...ctx.elements];

    // 🔥 FIX: Record state AFTER all data is updated
    ctx.recordStateChange('Footer updated');

    // Trigger autosave for website-level footer
    ctx.scheduleAutosave(['/website/footer']);
  }
  /**
   * Updates the website header configuration in the current context, preserving existing settings and triggering preview/state updates.
   * @example
   * nHeaderChange(data, ctx)
   * void
   * @param {HeaderData} data - New header data containing logo, navigation, CTA button, and background settings.
   * @param {InfoContext} ctx - Editing context that stores the current website state, preview elements, and autosave controls.
   * @returns {void} This function does not return a value.
   */
  onHeaderChange(data: HeaderData, ctx: InfoContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showHeaderPanel) {
      return;
    }

    ctx.headerData = data;

    if (!ctx.json) ctx.json = {};

    // Get existing header data to preserve properties like top_border, navigation_style, layout
    const existingHeader =
      ctx.website?.json_data?.website?.header || ctx.json?.website?.header || {};

    // Preserve hash links for button navigation in editor preview
    const buttonNavigation =
      data.ctaButton?.buttonNavigation ?? linkToButtonNavigation(data.ctaButton?.link);
    const buttonLink = buttonNavigationToHashLink(buttonNavigation);
    const buttonApiLink = buttonNavigationToApiLink(buttonNavigation);

    // Prepare header JSON structure matching API format
    const hJson: any = {
      // Preserve existing properties
      ...existingHeader,
      // Update logo
      logo: {
        type: data.logo?.image ? 'image' : 'text',
        text: data.logo?.text || 'Logo',
        link: (data.logo as any)?.link || '/',
        url: data.logo?.image || '',
        alt: data.logo?.alt || 'Logo',
        // Preserve existing logo style if it exists
        style: existingHeader.logo?.style || {
          background: 'transparent',
          border: '2px solid #3B82F6',
          color: '#000000',
          padding: '10px 15px',
          border_radius: '6px',
          font_size: '20px',
          font_weight: '600',
        },
      },
      // Update navigation - convert # links to / format for API
      navigation:
        data.navigation?.map((nav: any) => {
          let link = nav.link || '';
          // Convert # links to / format (e.g., #express-wash-packages -> /express-washes)
          if (link.startsWith('#')) {
            const pageType = link.substring(1);
            // Map page types to API link format
            const linkMap: Record<string, string> = {
              home: '/',
              'express-wash-packages': '/express-washes',
              'express-auto-details': '/express-details',
              'fleet-management': '/fleets',
              coupons: '/coupons',
            };
            link = linkMap[pageType] || `/${pageType.replace(/-/g, '-')}`;
          }
          return {
            label: nav.label,
            link: link,
            active: nav.active || false,
          };
        }) || [],
      // Update CTA button with proper API structure
      // Preserve hash links (#expresswashes) for navigation in editor preview
      cta_button: {
        label: data.ctaButton?.text || 'Book Wash',
        link: buttonApiLink,
        navigation_target: buttonNavigation,
        style: {
          // Use new values if provided, otherwise preserve existing, otherwise use defaults
          background_color:
            data.ctaButton?.backgroundColor !== undefined
              ? data.ctaButton.backgroundColor
              : existingHeader.cta_button?.style?.background_color || '#3B82F6',
          text_color:
            data.ctaButton?.textColor !== undefined
              ? data.ctaButton.textColor
              : existingHeader.cta_button?.style?.text_color || '#FFFFFF',
          border_radius: existingHeader.cta_button?.style?.border_radius || '8px',
          font_size: existingHeader.cta_button?.style?.font_size || '16px',
          font_weight: existingHeader.cta_button?.style?.font_weight || '600',
          padding: existingHeader.cta_button?.style?.padding || '0.75rem 1.5rem',
        },
      },
      background_color: data.backgroundColor || existingHeader.background_color || '#F9FAFB',
    };

    // Update website-level header (shared across all pages) - HIGHEST PRIORITY
    if (!ctx.website) ctx.website = {};
    if (!ctx.website.json_data) ctx.website.json_data = {};
    if (!ctx.website.json_data.website) ctx.website.json_data.website = {};
    ctx.website.json_data.website.header = hJson;

    this.logger.debug('Header updated - Button colors:', {
      backgroundColor: data.ctaButton?.backgroundColor,
      textColor: data.ctaButton?.textColor,
      savedBackgroundColor: hJson.cta_button.style.background_color,
      savedTextColor: hJson.cta_button.style.text_color,
    });

    // Also update currentJsonData for backward compatibility
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};
    ctx.json.page.sections.header = {
      logo: data.logo,
      navigation: data.navigation,
      buttons: [
        {
          ...data.ctaButton,
          buttonNavigation,
          link: buttonLink,
        },
      ],
      backgroundColor: data.backgroundColor,
    };

    // Update website-level in currentJsonData as well
    if (!ctx.json.website) ctx.json.website = {};
    ctx.json.website.header = hJson;

    // Update preview element for all pages
    const hElement = ctx.elements.find(e => e.id === 'header');
    if (hElement) {
      // Create a new object to trigger change detection
      hElement.content = {
        ...hElement.content,
        logo: data.logo,
        navigation: data.navigation,
        backgroundColor: data.backgroundColor,
        ctaButton: {
          ...data.ctaButton,
          buttonNavigation,
          link: buttonLink,
          backgroundColor: data.ctaButton?.backgroundColor,
          textColor: data.ctaButton?.textColor,
        },
      } as any;

      // Force update websiteElements array to trigger change detection
      ctx.elements = [...ctx.elements];
    }

    // Update editable JSON
    try {
      /* intentionally empty */
    } catch {
      /* ignore */
    }

    // 🔥 FIX: Record state AFTER all data is updated
    ctx.recordStateChange('Header updated');

    // Trigger autosave - save to website-level header
    ctx.scheduleAutosave(['/website/header']);
  }
  /**
   * Updates the contact section data, layout, and preview state when contact panel data changes.
   * @example
   * nContactChange(data, ctx)
   * void
   * @param {ContactData} data - The contact section data used to update content, layout, links, and business details.
   * @param {InfoContext} ctx - The editing context containing JSON state, preview elements, autosave, and panel state.
   * @returns {void} No return value.
   **/
  onContactChange(data: ContactData, ctx: InfoContext): void {
    // Only process changes when the panel is actually open
    if (!ctx.showContactPanel) {
      return;
    }

    ctx.contactPanelData = data;

    if (!ctx.json) ctx.json = {};

    // Ensure page.sections structure exists
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    // Update in page.sections.contact_section (new structure)
    if (!ctx.json.page.sections.contact_section) {
      ctx.json.page.sections.contact_section = {};
    }
    const contactSection = ctx.json.page.sections.contact_section;

    // Preserve existing properties (layout, active, etc.) and update content
    const titleStylePayload = data.titleStyle
      ? {
          fontWeight: data.titleStyle.fontWeight || null,
          fontStyle: data.titleStyle.fontStyle || null,
          textDecoration: data.titleStyle.textDecoration || null,
          textAlign: data.titleStyle.textAlign || null,
        }
      : null;

    if (titleStylePayload) {
      if (!contactSection.title || typeof contactSection.title !== 'object') {
        contactSection.title = { label: data.title, style: titleStylePayload };
      } else {
        contactSection.title.label = data.title;
        contactSection.title.style = titleStylePayload;
      }
    } else if (typeof contactSection.title === 'object' && contactSection.title !== null) {
      contactSection.title.label = data.title;
    } else {
      contactSection.title = { label: data.title };
    }

    contactSection.subtitle = data.subtitle || '';

    // API format: details.address, details.phone, details.email
    const address = data.contactInfo?.address;
    const addressString =
      typeof address === 'string'
        ? address
        : address && typeof address === 'object'
          ? this.getFullAddressFromContactData(data)
          : '';

    const phoneValue = data.contactInfo?.phone || '';

    const editorHours = data.businessHours || [];

    contactSection.details = {
      address: addressString || '',
      phone: phoneValue,
      contact: phoneValue,
      email: data.contactInfo?.email || '',
      operating_hours: toOperatingHourStrings(editorHours),
    };

    // Update map embed URL
    if (data.map?.embedLink) {
      contactSection.map_embed_url = data.map.embedLink;
    } else if (data.map?.imageUrl) {
      contactSection.map_embed_url = data.map.imageUrl;
    }

    // Update social links
    contactSection.social_links = (data.socialLinks || []).map((social: any) => ({
      platform: social.platform || '',
      icon: social.icon || social.platform?.toLowerCase() || '',
      link: social.url || (social as any).link || '',
    }));

    // Update business hours in every stored format so preview/publish do not keep template schedule
    contactSection.businessHours = editorHours;
    contactSection.operating_hours = {
      ...(contactSection.operating_hours || {}),
      schedule: toOperatingHoursSchedule(editorHours),
    };

    // Keep active state if it exists
    if (contactSection.active === undefined) {
      contactSection.active = true;
    }

    // Update contact element content for preview
    const contactElement = ctx.elements.find(e => e.id === 'contact');
    if (contactElement) {
      const existingLayout = (contactElement.content as any)?.layout;
      const layoutType = contactSection.layout?.type || data.layout || existingLayout || 'layout_1';

      contactElement.content = {
        ...contactElement.content,
        title: data.title,
        titleStyle: data.titleStyle || undefined,
        description: data.subtitle || '',
        subtitle: data.subtitle || '',
        address: addressString || '',
        phone: phoneValue,
        email: data.contactInfo?.email || '',
        mapEmbedUrl:
          data.map?.embedLink || data.map?.imageUrl || contactSection.map_embed_url || '',
        socialLinks: (data.socialLinks || []).map((social: any) => ({
          platform: social.platform || '',
          url: social.url || social.link || '',
          icon: social.icon || '',
          label: social.label || social.platform || '',
        })),
        businessHours: data.businessHours || [],
        backgroundColor: data.backgroundColor,
        layout: layoutType,
      } as any;

      ctx.elements = [...ctx.elements];
    }

    // Trigger autosave
    ctx.scheduleAutosave(['/sections/contact']);

    // Preserve layout if it exists, or set default based on template
    if (!contactSection.layout) {
      // Determine default layout based on template type
      const templateName = ctx.templateData?.name?.toLowerCase() || '';
      const defaultLayoutType = templateName.includes('dark') ? 'layout_2' : 'layout_1';

      contactSection.layout = {
        type: data.layout || defaultLayoutType,
        alignment: 'center',
        padding: '80px 100px',
        background_color: data.backgroundColor || '#FFFFFF',
      };
    } else {
      // Update layout type if provided, otherwise preserve existing
      if (data.layout) {
        contactSection.layout.type = data.layout;
      } else if (!contactSection.layout.type) {
        // Set default if no type exists
        const templateName = ctx.templateData?.name?.toLowerCase() || '';
        contactSection.layout.type = templateName.includes('dark') ? 'layout_2' : 'layout_1';
      }

      // Update background color in layout if it exists
      if (contactSection.layout) {
        contactSection.layout.background_color =
          data.backgroundColor || contactSection.layout.background_color || '#FFFFFF';
      }
    }

    // Update editable JSON
    try {
      /* intentionally empty */
    } catch {
      /* ignore */
    }

    // 🔥 FIX: Record state AFTER all data is updated
    ctx.recordStateChange('Contact section updated');

    // Trigger autosave
    ctx.scheduleAutosave(['/contact']);
  }

  private findInfoElement(
    elements: WebsiteElement[],
    sectionKey: string,
    elementId: string
  ): WebsiteElement | undefined {
    return elements.find(
      el =>
        el.id === elementId ||
        (el as any).originalKey === sectionKey ||
        (sectionKey === 'info_section' && el.id === 'welcome') ||
        (sectionKey === 'info_section_1' && (el.id === 'services' || el.type === 'services'))
    );
  }
}
